import { istAblehnung } from '../../persistence/anhaenge';
import type { MultipartUploadPlugin } from '../../platform/multipart';
import type { LogKind } from '../../platform/storage';
import { felderAusName, mimeVonName, multipartSender, type MultipartSender } from '../multipart';
import { basicKopfzeile } from './client';
import { rohkennung } from './kennung';

/**
 * Dateien zum Classic-Server hochladen — Bildschirmabzüge, Ton, Video, Handschrift.
 *
 * **Was der Server erwartet** (abgelesen am Aufruf des Classic-Clients, Angabe des Nutzers vom
 * 2026-09-12): `POST {host}/rest/screenshots/upload` als `multipart/form-data` mit der Datei
 * unter `file` und dreizehn weiteren Feldern. Der Name des Endpunkts sagt „screenshots", der
 * Classic-Client schickt darüber aber **jede** Art von Datei — die Liste der Medientypen, die er
 * dabei bildet, führt neben `png` und `gif` auch `caf`, `mp3` und `mp4`. Es ist also der
 * allgemeine Datei-Upload und nicht nur der für Abzüge.
 *
 * **Woher die Angaben kommen.** Der Classic-Client liest sie aus dem **Dateinamen**: alles vor dem
 * ersten `_` ist die Art, danach folgen `schlüssel=wert`-Paare. Unsere Ablage
 * (`platform/storage.ts`) schreibt dieselbe Bauform, aber mit unseren Namen (`examID`,
 * `candidateID`) und ohne Art im Namen — die steht bei uns im Ordner. Diese Datei ist die
 * Übersetzung dazwischen, und nur sie kennt beide Seiten.
 *
 * ⚠️ **Die Kennungen müssen durch `rohkennung()`.** Der Server kennt nur Zahlen. Steht im
 * Classic-Modus eine Relay-Prüfung in der Ablage, ist ihre UUID **keine** Kennung, und der
 * Abzug ginge sonst unter einer fremden Prüfung ein (s. `kennung.ts`).
 */

/** Was Classic als `version` erwartet. Feste Zahl, kein Bezug zur Fassung der App. */
export const CLASSIC_UPLOAD_VERSION = '2.0';

/** Der Pfad des Uploads. */
export const CLASSIC_UPLOAD_PFAD = '/rest/screenshots/upload';

/**
 * Unsere Zeitmarke in die des Classic-Clients.
 *
 * ⚠️ **Ein Strich Unterschied.** `storage.ts` schreibt `yyyy-MM-dd-HH-mm-ss-SSS`, der
 * Classic-Client baut `yyyy-MM-dd-HH-mm-ss` **unmittelbar gefolgt** von den Millisekunden. Wer das
 * übersieht, schickt eine Marke, die neben den Abzügen eines Altgeräts falsch einsortiert.
 */
export function classicZeitstempel(unsere: string): string {
  const treffer = /^(\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2})-(\d{3})$/.exec(unsere);
  return treffer ? treffer[1] + treffer[2] : unsere;
}

/**
 * Die Zeitmarke im Format der alten App **je Dateiart** (`docs/classic-rest-api.md` §7):
 * Bilder `yyyy-MM-dd-HH-mm-ss-SSS`, Ton und Video `yyyy-MM-dd-HH-mm-ssSSS`.
 */
export function classicDatum(unsere: string, art: ClassicDateiart): string {
  return art === 'audio' || art === 'video' ? classicZeitstempel(unsere) : unsere;
}

/**
 * Was die alte App als `type` schickt — und als Anfang des Dateinamens. Der Server sortiert
 * die Dateien danach; mit Stages eigenen Arten (`photo`, `audio` …) lägen sie dort, wo die
 * Auswertung sie nicht sucht. Die Handschrift heisst nach ihrem Bezug: an einer Aufgabe
 * `task-comments`, an der Station `station-comments`.
 */
export function classicTyp(art: ClassicDateiart, mitAufgabe: boolean): string {
  switch (art) {
    case 'screenshot':
      return 'screenshots';
    case 'photo':
      return 'photo-comments';
    case 'audio':
      return 'audio-comments';
    case 'video':
      return 'video-comments';
    case 'handwriting':
      return mitAufgabe ? 'task-comments' : 'station-comments';
    case 'log':
      return 'log';
  }
}

/**
 * Der Dateiname nach dem Schema der alten App:
 * `{type}_date=…_osceID=…_stationID=…_taskID=…_answerID=…_examinerID=…_examineeID=….{ext}`.
 * Nur Zahlen, keine Doppelpunkt-Kennungen — der Server liest die Zuordnung aus dem Namen.
 */
export function classicDateiname(felder: Record<string, string>, endung: string): string {
  return (
    felder.type +
    '_date=' + felder.date +
    '_osceID=' + felder.osceID +
    '_stationID=' + felder.stationID +
    '_taskID=' + felder.taskID +
    '_answerID=' + felder.answerID +
    '_examinerID=' + felder.examinerID +
    '_examineeID=' + felder.examineeID +
    '.' + endung
  );
}

/** Was eine Kennung unserer Ablage auf dem Server ist — oder `-1`, wenn wir es nicht wissen. */
function zahlOderMinusEins(unsere: string | undefined): string {
  const zahl = rohkennung(unsere);
  return zahl === null ? '-1' : String(zahl);
}

/**
 * Was im Feld `type` steht.
 *
 * Die Arten unserer Ablage, dazu `log` für die Tagesprotokolle — die sind keine Aufnahme zu
 * einer Prüfungsleistung und hätten unter keiner der anderen Bezeichnungen etwas verloren.
 */
export type ClassicDateiart = LogKind | 'log';

export interface FelderArgs {
  /** Der blosse Dateiname, ohne Ordner. */
  dateiname: string;
  /** Was für eine Datei es ist; wird zum Feld `type`. */
  art: ClassicDateiart;
  /**
   * Die Station aus dem laufenden Betrieb. Fehlt sie, wird sie aus dem Dateinamen genommen —
   * dort steht sie seit dem 2026-09-14 (`platform/storage.ts::buildFileName`).
   */
  stationId?: string;
  deviceName: string;
  deviceID: string;
  /** Der wievielte Aufruf dieses Stapels, 0-basiert. */
  index: number;
  /** Wie viele Dateien der Stapel hat. */
  anzahl: number;
}

/**
 * Die dreizehn Felder neben `file`.
 *
 * `progressTick` und `progressFinished` sind kein Beiwerk: an ihnen sieht die Konsole des
 * Prüfungsleiters, wie weit ein Gerät ist und wann es fertig war.
 */
export function baueFelder(args: FelderArgs): Record<string, string> {
  const ausName = felderAusName(args.dateiname);
  const taskID = zahlOderMinusEins(ausName.taskID ?? ausName.examItemID ?? ausName.itemID);
  return {
    deviceName: args.deviceName,
    deviceID: args.deviceID,
    type: classicTyp(args.art, taskID !== '-1'),
    date: classicDatum(ausName.date ?? '', args.art),
    osceID: zahlOderMinusEins(ausName.examID),
    /*
     * ⚠️ **Der übergebene Wert hat Vorrang, der Name ist der Rückfall.** Bis zum 2026-09-14
     * stand die Station in keinem Dateinamen; gab der Aufrufer sie nicht mit, ging `-1` hinaus
     * und der Classic-Server wusste nicht, wo der Abzug entstand. Der übergebene Wert stammt aus
     * dem laufenden Betrieb, der Name aus dem Augenblick der Aufnahme — bei einem Nachzügler
     * nach einem Stationswechsel ist der Name der richtigere, aber der Betrieb ist der
     * genauere, solange er etwas weiss.
     */
    /*
     * ⚠️ **Auch die Relay-Begriffe werden gelesen.** Im Classic-Modus schreibt die Ablage
     * `stationID`/`taskID` (seit 2026-09-17, `platform/storage.ts`), aber nach einem
     * Moduswechsel liegen Dateien mit `examItemContainerID`/`examItemID` im selben Ordner.
     * Sie gehören demselben Prüfling und derselben Station — nur der Name ist ein anderer.
     */
    stationID: zahlOderMinusEins(
      args.stationId ?? ausName.stationID ?? ausName.examItemContainerID,
    ),
    taskID,
    // Unsere Ablage kennt keine Antwortzeile je Datei — eine Aufnahme hängt an der Aufgabe.
    answerID: '-1',
    examinerID: zahlOderMinusEins(ausName.examinerID),
    examineeID: zahlOderMinusEins(ausName.candidateID),
    progressTick: String((args.index + 1) / args.anzahl),
    progressFinished: String(args.index + 1 === args.anzahl),
    version: CLASSIC_UPLOAD_VERSION,
  };
}

export interface HochzuladendeDatei {
  /** Pfad unterhalb von Documents, wie ihn `platform/storage.ts` zurückgibt. */
  pfad: string;
  art: ClassicDateiart;
  /** Die Station aus dem laufenden Betrieb; fehlt sie, steht sie im Dateinamen. */
  stationId?: string;
}

export interface HochladenDeps {
  /** Der Server, ohne abschliessenden Schrägstrich. */
  basisUrl: string;
  dateien: HochzuladendeDatei[];
  leseBase64: (pfad: string) => Promise<string>;
  deviceName: string;
  deviceID: string;
  senden: MultipartSender;
  log: (zeile: string) => Promise<void>;
  /**
   * Wandelt eine Datei vor dem Senden in das Format, das der Classic-Server erwartet: SVG
   * (Handschrift, Markierebene) zu PNG, Ton (m4a) zu CAF. `null` heisst: unverändert senden.
   * Fehlt es (Tests), geht alles unverändert hinaus.
   */
  wandle?: (base64: string, endung: string) => Promise<{ base64: string; endung: string } | null>;
  /**
   * Wohin der Ausgang je Datei gemeldet wird.
   *
   * ⚠️ **Hineingereicht, nicht importiert.** Diese Datei kennt nur den Transport; die
   * Buchführung liegt in `persistence/anhaenge.ts` und wird von `anforderungen.ts`
   * angebunden. So bleibt das Hochladen ohne Ablage prüfbar.
   */
  merke?: {
    erfolg: (datei: HochzuladendeDatei) => Promise<void>;
    fehlschlag: (
      datei: HochzuladendeDatei,
      fehler: string,
      abgewiesen: boolean,
    ) => Promise<void>;
  };
}

/** Nur der Dateiname, ohne Ordner. */
function nameVon(pfad: string): string {
  const strich = pfad.lastIndexOf('/');
  return strich < 0 ? pfad : pfad.slice(strich + 1);
}

/**
 * Den ganzen Stapel hochladen.
 *
 * ⚠️ **Es wird nichts gelöscht, aber seit dem 2026-09-22 vermerkt.** Vorher führte der
 * Classic-Weg keine Buchführung: jede Anforderung lud **alles** erneut hoch, und wie viele
 * Dateien noch ausstehen, war nicht zu beantworten. Jetzt schreibt jeder Ausgang in dieselbe
 * Tabelle wie im Relay-Weg (`persistence/anhaenge.ts`) — daraus entsteht die Zahl in der
 * Kopfzeile, und der Client kann selbst nachschieben, was noch nicht durch ist (Vorgabe des
 * Nutzers, 2026-09-22). Die Dateien bleiben als Beleg auf dem iPad.
 *
 * ⚠️ **Ein Fehlschlag hält den Stapel nicht auf.** Gezählt wird beides, und beides steht im
 * Protokoll — eine einzelne unlesbare Datei darf die übrigen nicht mitnehmen.
 */
export async function ladeHoch(deps: HochladenDeps): Promise<{
  gesendet: number;
  gescheitert: number;
}> {
  const anzahl = deps.dateien.length;
  let gesendet = 0;
  let gescheitert = 0;
  if (anzahl === 0) return { gesendet, gescheitert };

  for (let index = 0; index < anzahl; index += 1) {
    const datei = deps.dateien[index];
    const dateiname = nameVon(datei.pfad);
    try {
      let base64 = await deps.leseBase64(datei.pfad);
      let endung = (/\.([a-z0-9]+)$/i.exec(dateiname)?.[1] ?? 'bin').toLowerCase();
      const gewandelt = deps.wandle ? await deps.wandle(base64, endung) : null;
      if (gewandelt) ({ base64, endung } = gewandelt);
      const felder = baueFelder({
        dateiname,
        art: datei.art,
        stationId: datei.stationId,
        deviceName: deps.deviceName,
        deviceID: deps.deviceID,
        index,
        anzahl,
      });
      // Die Protokolle tragen keinen Prüfungsbezug; sie behalten ihren Namen.
      const sendename = datei.art === 'log' ? dateiname : classicDateiname(felder, endung);
      const antwort = await deps.senden({
        url: deps.basisUrl.replace(/\/+$/, '') + CLASSIC_UPLOAD_PFAD,
        felder,
        datei: { name: sendename, mime: mimeVonName(sendename), base64 },
      });

      // ⚠️ **Status 200 heisst noch nicht angekommen.** Der Server antwortet
      // `{"success":false}`, wenn die empfangene Länge nicht zur angekündigten passt — eine
      // abgeschnittene Datei sähe sonst wie ein Erfolg aus.
      const abschlaegig = antwort.text.replace(/\s+/g, '').includes('"success":false');
      if (!antwort.ok || abschlaegig) {
        gescheitert += 1;
        const grund = 'Status ' + String(antwort.status) +
          (abschlaegig ? ', der Server meldet success:false' : '');
        await deps.log('Upload (Classic) abgelehnt: ' + dateiname + ' — ' + grund);
        /*
         * ⚠️ **Abgewiesen nur bei 4xx.** Der Rückzug deckelt bei 60 s; zählte ein Netzfehler
         * mit, wäre eine Datei nach viereinhalb Minuten ohne Netz für immer aufgegeben —
         * genau in der Lage, in der Wiederholen richtig ist (s. `ABLEHNUNGSGRENZE`). Ein
         * `success:false` ist dagegen eine überlegte Antwort und zählt.
         */
        const abgewiesen = abschlaegig || istAblehnung(antwort.status);
        await deps.merke?.fehlschlag(datei, grund, abgewiesen);
        continue;
      }
      gesendet += 1;
      await deps.merke?.erfolg(datei);
    } catch (fehler) {
      gescheitert += 1;
      await deps.log('Upload (Classic) gescheitert: ' + dateiname + ' — ' + String(fehler));
      // Kein Abweisen: hier kam gar keine Antwort (kein Netz, Zeitablauf).
      await deps.merke?.fehlschlag(datei, String(fehler), false);
    }
  }

  await deps.log(
    'Upload (Classic): ' + String(gesendet) + ' von ' + String(anzahl) + ' Dateien übertragen' +
      (gescheitert > 0 ? ', ' + String(gescheitert) + ' offen' : '') + '.',
  );
  return { gesendet, gescheitert };
}

/**
 * Der Transport für **Classic**: derselbe wie überall, nur mit der Basic-Kopfzeile davor.
 *
 * Die Wahl zwischen `fetch` und dem nativen Plugin trifft `server/multipart.ts` — hier steht
 * nur, womit Classic sich anmeldet.
 */
export function classicSender(
  zugang: { baseUrl: string; login: string; password: string },
  optionen: {
    nativ?: boolean;
    plugin?: MultipartUploadPlugin;
    hole?: typeof fetch;
  } = {},
): MultipartSender {
  return multipartSender(
    { Authorization: basicKopfzeile(zugang.login, zugang.password) },
    optionen,
  );
}

// Weitergereicht, damit die Aufrufer des Classic-Gleises an einer Stelle bleiben können.
export { felderAusName, mimeVonName } from '../multipart';
export type { MultipartAufruf, MultipartSender } from '../multipart';
